import { FC, useEffect } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useHistory } from 'react-router-dom';
import { useMutation, useQuery } from '@apollo/client';
import { Box, Flex, Stack, VStack } from '@chakra-ui/react';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';

import Button from 'components/_core/Button';
import * as Form from 'components/_core/form';
import Heading from 'components/_core/Heading';
import SunshineCard from 'components/_core/SunshineCard';
import SquircleIcon from 'components/_core/SunshineSquircleIcon';
import Text from 'components/_core/Text';
import BackButton from 'components/BackButton';
import Loader from 'components/Loader';
import SelectOption from 'components/SelectOption';
import useToast from 'components/ToastProvider/useToast';
import { logEvent } from 'features/Analytics/analytics';
import TrackView from 'features/Analytics/TrackView';
import * as Layout from 'layouts/StepperLayout';

import * as routes from '../../routes';
import {
  CreateIntercomConversationDocument,
  GetAskQuestionDocument,
} from './getAskQuestion.gql';
import locales from './locales';

const AskQuestion: FC = () => {
  const history = useHistory();
  const { showToast } = useToast();

  const { data, loading } = useQuery(GetAskQuestionDocument);
  const [createIntercomConversation, { loading: loadingMutation }] =
    useMutation(CreateIntercomConversationDocument);

  const schema = z.object({
    category: z.string().min(1),
    contactReason: z.string().min(1),
    message: z.string().min(1),
    subCategory: z.string(),
  });

  const {
    control,
    formState: { isValid },
    handleSubmit,
    register,
    setValue,
    watch,
  } = useForm({
    defaultValues: {
      category: '',
      contactReason: '',
      message: '',
      subCategory: '',
    },
    mode: 'onSubmit',
    resolver: zodResolver(schema),
  });

  const helpContactTree = data?.helpContactTree;

  const categoryOptions =
    helpContactTree?.find(
      (contactReason) => contactReason.value === watch('contactReason'),
    )?.categories ?? [];

  const subCategoryOptions =
    categoryOptions.find((category) => category.value === watch('category'))
      ?.categories ?? [];

  const hasSubCategories = subCategoryOptions.length > 0;

  const categoriesArticles =
    categoryOptions.find((category) => category.value === watch('category'))
      ?.links ?? [];

  const subCategoriesArticles =
    subCategoryOptions.find(
      (subCategory) => subCategory.value === watch('subCategory'),
    )?.links ?? [];

  const articles =
    categoriesArticles.length > 0 ? categoriesArticles : subCategoriesArticles;

  const hasArticles = Boolean(articles.length > 0);

  useEffect(() => {
    if (hasArticles) {
      logEvent({
        name: 'Articles Shown Ask Question',
      });
    }
  }, [hasArticles]);

  if (loading || !data) {
    return <Loader />;
  }

  const onSubmit = handleSubmit(async (payload) => {
    try {
      await createIntercomConversation({
        variables: { input: payload },
      });
      history.push(routes.askQuestionConfirmation);
    } catch (err) {
      showToast({
        message: locales.error,
        type: 'error',
      });
    }
  });

  return (
    <Layout.Root>
      <TrackView pageName="AskQuestion" />
      <Layout.Body>
        <Layout.HeaderButtonsContainer>
          <BackButton
            onClick={() => {
              logEvent({ name: 'Back Button Clicked Ask Question' });
              history.goBack();
            }}
          />
        </Layout.HeaderButtonsContainer>

        <Layout.BodyScrollableSection>
          <Stack paddingBottom="space-40" spacing="space-8">
            <Heading size="lg">{locales.title}</Heading>
            <Text>{locales.subtitle}</Text>
          </Stack>

          <Box>
            <Flex direction="column">
              <Flex
                as="form"
                direction="column"
                height="full"
                justify="space-between"
                noValidate
                onSubmit={onSubmit}
              >
                <VStack
                  alignItems="stretch"
                  paddingBottom="space-40"
                  spacing="space-32"
                >
                  <Form.Field label={locales.form.contactReason}>
                    <Controller
                      control={control}
                      name="contactReason"
                      render={({ field }) => {
                        const selectedOption = helpContactTree?.find(
                          (contactReason) =>
                            contactReason.value === field.value,
                        );

                        return (
                          <Form.SunshineSelect
                            components={{ Option: SelectOption }}
                            onChange={(option) => {
                              logEvent({
                                name: `${option?.value} Contact Reason Selected Ask Question`,
                              });
                              setValue('category', '');
                              setValue('subCategory', '');
                              field.onChange(option?.value);
                            }}
                            onMenuOpen={() =>
                              logEvent({
                                name: `Contact Reason Select Opened Ask Question`,
                              })
                            }
                            options={helpContactTree}
                            placeholder={locales.form.selectContactReason}
                            value={selectedOption}
                          />
                        );
                      }}
                    />
                  </Form.Field>

                  <Form.Field
                    display={watch('contactReason') ? undefined : 'none'}
                    label={locales.form.category}
                  >
                    <Controller
                      control={control}
                      name="category"
                      render={({ field }) => {
                        const selectedOption =
                          categoryOptions.find(
                            (option) => option.value === field.value,
                          ) ?? null;

                        return (
                          <Form.SunshineSelect
                            components={{ Option: SelectOption }}
                            onChange={(option) => {
                              logEvent({
                                name: `${option?.value} Category Selected Ask Question`,
                              });
                              field.onChange(option?.value);
                              setValue('subCategory', '');
                            }}
                            onMenuOpen={() =>
                              logEvent({
                                name: `Category Select Opened Ask Question`,
                              })
                            }
                            options={categoryOptions}
                            placeholder={locales.form.selectCategory}
                            value={selectedOption}
                          />
                        );
                      }}
                    />
                  </Form.Field>

                  <Form.Field
                    display={hasSubCategories ? undefined : 'none'}
                    label={locales.form.subCategory}
                  >
                    <Controller
                      control={control}
                      name="subCategory"
                      render={({ field }) => {
                        const selectedOption =
                          subCategoryOptions.find(
                            (option) => option.value === field.value,
                          ) ?? null;

                        return (
                          <Form.SunshineSelect
                            components={{ Option: SelectOption }}
                            onChange={(option) => {
                              logEvent({
                                name: `${option?.value} Subcategory Selected Ask Question`,
                              });

                              field.onChange(option?.value);
                            }}
                            onMenuOpen={() =>
                              logEvent({
                                name: `SubCategory Select Opened Ask Question`,
                              })
                            }
                            options={subCategoryOptions}
                            placeholder={locales.form.selectSubCategory}
                            value={selectedOption}
                          />
                        );
                      }}
                    />
                  </Form.Field>

                  {hasArticles ? (
                    <Flex flexDirection="column" gap={16}>
                      <Text fontWeight="weight-500" variant="light">
                        {locales.form.articles}
                      </Text>
                      <SunshineCard.Group>
                        {articles.map(({ label, url }) => (
                          <SunshineCard
                            alignItems="center"
                            as="a"
                            href={url}
                            key={label}
                            onClick={() => {
                              logEvent({
                                name: `${label} Article Clicked Ask Question`,
                              });
                            }}
                            rel="noopener noreferrer"
                            target="_blank"
                          >
                            <SunshineCard.Slot name="aside">
                              <SquircleIcon name="file" />
                            </SunshineCard.Slot>

                            <SunshineCard.Content>
                              <SunshineCard.Title>{label}</SunshineCard.Title>
                            </SunshineCard.Content>

                            <SunshineCard.Slot name="action">
                              <SunshineCard.SunshineIcon name="chevron-right" />
                            </SunshineCard.Slot>
                          </SunshineCard>
                        ))}
                      </SunshineCard.Group>
                    </Flex>
                  ) : null}

                  <Form.Field
                    display={
                      hasArticles || watch('subCategory') === 'OTHER'
                        ? undefined
                        : 'none'
                    }
                  >
                    <Form.Textarea
                      placeholder={locales.form.question}
                      rows={5}
                      {...register('message')}
                    />
                  </Form.Field>

                  <Box>
                    <Button
                      isDisabled={!isValid}
                      isLoading={loadingMutation}
                      onClick={() => {
                        logEvent({
                          name: 'Submit Button Clicked Ask Question',
                        });
                      }}
                      type="submit"
                    >
                      {locales.form.submit}
                    </Button>
                  </Box>
                </VStack>
              </Flex>
            </Flex>
          </Box>
        </Layout.BodyScrollableSection>
      </Layout.Body>
    </Layout.Root>
  );
};

export default AskQuestion;
